import { useTranslation } from 'react-i18next';
import { Chip, type ChipTone, type MeterTone } from '@koyomi/ui';
import type { Band, InfraStatus, MetricUnit, Thresholds } from './types.ts';

export const STATUS_RANK: Record<InfraStatus, number> = { unknown: 0, green: 1, yellow: 2, red: 3 };
export const statusTone = (s: InfraStatus): ChipTone & MeterTone => (s === 'green' ? 'accent' : s === 'yellow' ? 'warn' : s === 'red' ? 'danger' : 'neutral');
/** Pior status conhecido; `unknown` só quando nada foi medido. */
export const worstStatus = (list: InfraStatus[]): InfraStatus => list.reduce<InfraStatus>((w, s) => (STATUS_RANK[s] > STATUS_RANK[w] ? s : w), 'unknown');

export function StatusChip({ status, label }: { status: InfraStatus; label?: string }) {
  const { t } = useTranslation('admin');
  return <Chip tone={statusTone(status)} dot={status === 'unknown' ? 'var(--color-faint)' : 'currentColor'}>{label ?? t(`infra.status.${status}`)}</Chip>;
}

/** Qualidade do número na Meta: GREEN/YELLOW/RED em chip colorido com o texto do nível. */
export function QualityChip({ rating }: { rating: string | null | undefined }) {
  const { t } = useTranslation('admin');
  if (!rating) return <Chip tone="neutral">{t('infra.meta.noRating')}</Chip>;
  const r = rating.toUpperCase();
  const tone: ChipTone = r === 'GREEN' ? 'accent' : r === 'YELLOW' ? 'warn' : r === 'RED' ? 'danger' : 'neutral';
  return <Chip tone={tone} dot="currentColor">{t(`infra.meta.quality.${r}`, { defaultValue: rating })}</Chip>;
}

export const isEnglish = (lang: string) => lang.startsWith('en');

export function fmtNum(v: number | null | undefined, locale: string, maxFrac?: number): string {
  if (v == null || !Number.isFinite(v)) return '—';
  const abs = Math.abs(v);
  const frac = maxFrac ?? (abs < 10 ? 2 : abs < 100 ? 1 : 0);
  return new Intl.NumberFormat(locale, { maximumFractionDigits: frac }).format(v);
}

export function fmtUnit(v: number | null | undefined, unit: MetricUnit | '/h' | 'GB', locale: string, text?: string): string {
  if (unit === 'text') return text ?? (v == null ? '—' : fmtNum(v, locale));
  if (v == null || !Number.isFinite(v)) return '—';
  switch (unit) {
    case '%': return `${fmtNum(v, locale, 1)}%`;
    case 'ms': return `${fmtNum(v, locale, v < 10 ? 1 : 0)} ms`;
    case 's': return `${fmtNum(v, locale, v < 10 ? 1 : 0)} s`;
    case 'MB': return v >= 1024 ? `${fmtNum(v / 1024, locale, 2)} GB` : `${fmtNum(v, locale, v < 10 ? 1 : 0)} MB`;
    case 'GB': return `${fmtNum(v, locale, 2)} GB`;
    case 'rpm': return `${fmtNum(v, locale, 1)}/min`;
    case '/h': return `${fmtNum(v, locale, 0)}/h`;
    default: return fmtNum(v, locale, 0);
  }
}

export const usd = (v: number | null | undefined, locale: string) => v == null ? '—' : new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(v);

export function fmtUptime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '—';
  const d = Math.floor(sec / 86400), h = Math.floor((sec % 86400) / 3600), m = Math.floor((sec % 3600) / 60);
  return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}min` : `${m} min`;
}

/** Limites editáveis, na ordem da tela. `path` é o caminho em Thresholds (e o `thresholdKey` das métricas). */
export const THRESHOLD_ROWS: { path: string; unit: '%' | 'ms' | 's' | '/h'; lowerWorse?: boolean }[] = [
  { path: 'redisMemoryPct', unit: '%' },
  { path: 'pgDiskPct', unit: '%' },
  { path: 'pgConnectionsPct', unit: '%' },
  { path: 'pgCacheHitPct', unit: '%', lowerWorse: true },
  { path: 'apiMemoryPct', unit: '%' },
  { path: 'apiCpuPct', unit: '%' },
  { path: 'eventLoopP99Ms', unit: 'ms' },
  { path: 'queueOldestWaitSec.ai', unit: 's' },
  { path: 'queueOldestWaitSec.default', unit: 's' },
  { path: 'replyLatencyP95Sec', unit: 's' },
  { path: 'llmRemainingRequestsPct', unit: '%', lowerWorse: true },
  { path: 'llm429PerHour', unit: '/h' },
];
export const LOWER_WORSE = new Set(THRESHOLD_ROWS.filter((r) => r.lowerWorse).map((r) => r.path));
/** Chave de tradução para um caminho com ponto (i18next usa o ponto como separador). */
export const i18nPath = (path: string) => path.replace(/\./g, '_');

export function getBand(th: Thresholds | undefined, path: string): Band | null {
  if (!th) return null;
  let cur: unknown = th;
  for (const part of path.split('.')) cur = cur && typeof cur === 'object' ? (cur as Record<string, unknown>)[part] : undefined;
  const b = cur as Partial<Band> | undefined;
  return b && typeof b.yellow === 'number' && typeof b.red === 'number' ? { yellow: b.yellow, red: b.red } : null;
}
export function setBand(th: Thresholds, path: string, band: Band): Thresholds {
  const [a, b] = path.split('.') as [string, string | undefined];
  if (!b) return { ...th, [a]: band };
  return { ...th, [a]: { ...(th as unknown as Record<string, Record<string, Band>>)[a], [b]: band } };
}
/** Amarelo antes do vermelho na direção em que a métrica piora. */
export const bandValid = (path: string, b: Band) => (LOWER_WORSE.has(path) ? b.yellow > b.red : b.yellow < b.red);

/** Métricas do gráfico de tendências (lista fixa) e o limite correspondente, quando houver. */
export const TREND_METRICS: { key: string; unit: MetricUnit; threshold?: string }[] = [
  { key: 'redis.memoryPct', unit: '%', threshold: 'redisMemoryPct' },
  { key: 'postgres.dbSizeMb', unit: 'MB' },
  { key: 'postgres.connectionsPct', unit: '%', threshold: 'pgConnectionsPct' },
  { key: 'postgres.cacheHitPct', unit: '%', threshold: 'pgCacheHitPct' },
  { key: 'api.eventLoopP99Ms', unit: 'ms', threshold: 'eventLoopP99Ms' },
  { key: 'api.cpuPct', unit: '%', threshold: 'apiCpuPct' },
  { key: 'api.memoryPct', unit: '%', threshold: 'apiMemoryPct' },
  { key: 'product.replyP95Sec', unit: 's', threshold: 'replyLatencyP95Sec' },
  { key: 'queues.ai.oldestWaitSec', unit: 's', threshold: 'queueOldestWaitSec.ai' },
  { key: 'capacity.peakRpm', unit: 'rpm' },
];
